import React, { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { ArrowLeft, ArrowRight, ChevronLeft, ChevronRight, Phone, Mail, MapPin, Calendar, Tag, MessageSquare, Trash2, Shield, ShieldCheck, Flag, Rocket, Pencil, Eye, MousePointerClick, CheckCircle2, FileText, X, ZoomIn, Store, Navigation, BadgeCheck, User as UserIcon, PlayCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { SUPPORT_EMAIL } from "../components/Footer";
import WishlistButton from "../components/WishlistButton";
import ShareButton from "../components/ShareButton";
import { canEmbed } from "../lib/videoEmbed";
import { playMedia } from "../components/GlobalMediaPlayer";
import { requireAuth } from "../components/SignInGate";
import MarkSoldDialog from "../components/MarkSoldDialog";
import Disclaimer from "../components/Disclaimer";
import ServicesMetaDisplay from "../components/ServicesMetaDisplay";
import ProductMetaExtrasDisplay from "../components/ProductMetaExtrasDisplay";
import VerifiedBadge from "../components/VerifiedBadge";
import SubscribeButton from "../components/SubscribeButton";
import ProtectedImage from "../components/ProtectedImage";
import ReportPhotoDialog from "../components/ReportPhotoDialog";
import { shouldShowConditionTag } from "../lib/categoryRules";
import { discountPercent, mrpShortLabel, canReview } from "../lib/pricingLabels";
import { maybeShowInterstitial } from "../lib/admob";import JobApplyDialog from "../components/JobApplyDialog";
import ReviewSection from "../components/ReviewSection";
import { trackView, trackClick } from "../lib/analytics";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
    AlertDialogTitle, AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { formatPrice, formatCompensationRange } from "../lib/currency";
import { useSeoMeta, SEO_BASE_URL } from "../lib/useSeoMeta";
import AdSenseSlot from "../components/AdSlot";
import AdukooAdSlot from "../components/AdukooAdSlot";
import AdminInlineEditor from "../components/admin/AdminInlineEditor";
import AdminAuditLog from "../components/AdminAuditLog";

export default function ListingDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { user, loading: authLoading } = useAuth();
    const [listing, setListing] = useState(null);
    const [activeImg, setActiveImg] = useState(0);
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [zoom, setZoom] = useState(1);
    const [error, setError] = useState("");

    // Keyboard navigation inside the lightbox: ← / → switch image, Esc closes,
    // +/- zoom in/out. No-op when lightbox is closed.
    useEffect(() => {
        if (!lightboxOpen || !listing) return;
        const imgsLen = (listing.images?.length || 0) || 1;
        const onKey = (e) => {
            if (e.key === "Escape") setLightboxOpen(false);
            else if (e.key === "ArrowLeft") { setActiveImg((i) => (i - 1 + imgsLen) % imgsLen); setZoom(1); }
            else if (e.key === "ArrowRight") { setActiveImg((i) => (i + 1) % imgsLen); setZoom(1); }
            else if (e.key === "+" || e.key === "=") setZoom((z) => Math.min(4, +(z + 0.25).toFixed(2)));
            else if (e.key === "-" || e.key === "_") setZoom((z) => Math.max(0.5, +(z - 0.25).toFixed(2)));
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [lightboxOpen, listing]);
    const [chatLoading, setChatLoading] = useState(false);
    const [boostBusy, setBoostBusy] = useState(false);
    const [contactReq, setContactReq] = useState(null); // { status: 'none'|'pending'|'approved'|'denied' }
    const [contactBusy, setContactBusy] = useState(false);

    const boostDaysLeft = (iso) => {
        if (!iso) return 0;
        return Math.max(0, Math.ceil((new Date(iso).getTime() - Date.now()) / 86400000));
    };

    // v78.10 — Prev / Next AD navigation (chevrons + edge swipe).
    // Fetches the immediately-adjacent ad in the same category from
    // /api/listings/{id}/neighbors so the user can browse the feed
    // without bouncing back to /browse.
    const [neighbors, setNeighbors] = useState({ prev: null, next: null });
    useEffect(() => {
        if (!listing?.id || !listing?.category) return;
        let alive = true;
        const qs = new URLSearchParams();
        if (listing.category) qs.set("category", listing.category);
        if (listing.country) qs.set("country", listing.country);
        api.get(`/listings/${listing.id}/neighbors?${qs.toString()}`)
            .then((r) => { if (alive) setNeighbors(r.data || { prev: null, next: null }); })
            .catch(() => { if (alive) setNeighbors({ prev: null, next: null }); });
        return () => { alive = false; };
    }, [listing?.id, listing?.category, listing?.country]);

    const goNeighbor = (which) => {
        const target = neighbors?.[which];
        if (!target?.id) return;
        // window.scrollTo(0,0) keeps the new ad starting from the top,
        // matching the muscle memory of a real feed swipe.
        window.scrollTo(0, 0);
        // v78.10.3 — Use `replace: true` so chevron / swipe navigation
        // does NOT accumulate history. Without this, swiping through 8
        // ads pushes 8 entries and the user's Back button has to walk
        // back through every one before reaching the originating
        // category page — which was the founder's complaint.
        navigate(`/listing/${target.id}`, { replace: true });
    };

    // Keyboard arrows for desktop power-users — only fire when the
    // lightbox is closed (lightbox has its own arrow handlers).
    useEffect(() => {
        if (lightboxOpen) return;
        const onKey = (e) => {
            if (e.target && (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA" || e.target.isContentEditable)) return;
            if (e.key === "ArrowLeft") goNeighbor("prev");
            else if (e.key === "ArrowRight") goNeighbor("next");
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lightboxOpen, neighbors]);

    // Edge swipe on the WHOLE page (mobile). We use a separate ref so the
    // image-gallery swipe (above) keeps working independently. We only
    // trigger an ad-jump if the gesture starts outside the gallery area
    // (i.e. on the body of the page) and is dominantly horizontal.
    const pageSwipeRef = useRef({ x: 0, y: 0, active: false });
    const onPageSwipeStart = (e) => {
        // Ignore gestures that begin inside the gallery — those flip images.
        if (e.target.closest && e.target.closest("[data-testid='listing-cover']")) return;
        const t = e.touches?.[0]; if (!t) return;
        pageSwipeRef.current = { x: t.clientX, y: t.clientY, active: true };
    };
    const onPageSwipeEnd = (e) => {
        const s = pageSwipeRef.current; if (!s.active) return;
        pageSwipeRef.current.active = false;
        const t = e.changedTouches?.[0]; if (!t) return;
        const dx = t.clientX - s.x;
        const dy = t.clientY - s.y;
        if (Math.abs(dx) < 80 || Math.abs(dx) < Math.abs(dy) * 1.5) return;
        if (dx < 0) goNeighbor("next");
        else goNeighbor("prev");
    };

    /** Touch-swipe handlers — used both on the cover and inside the lightbox.
     *  A horizontal drag of more than 40 px (and dominantly horizontal vs.
     *  vertical) flips to the next/previous image, mirroring Instagram /
     *  Amazon mobile galleries. */
    const swipeRef = useRef({ x: 0, y: 0, active: false });
    const onSwipeStart = (e) => {
        const t = e.touches?.[0];
        if (!t) return;
        swipeRef.current = { x: t.clientX, y: t.clientY, active: true };
    };
    const onSwipeEnd = (e) => {
        const s = swipeRef.current;
        if (!s.active) return;
        const t = e.changedTouches?.[0];
        if (!t) return;
        const dx = t.clientX - s.x;
        const dy = t.clientY - s.y;
        swipeRef.current.active = false;
        if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy)) return;
        const imgsLen = (listing?.images?.length || 0) || 1;
        if (imgsLen < 2) return;
        if (dx < 0) setActiveImg((i) => (i + 1) % imgsLen);
        else setActiveImg((i) => (i - 1 + imgsLen) % imgsLen);
        setZoom(1);
    };

    // v77.34 — Three-tier boost picker (7/15/30) replaces the single
    // hardcoded call. Default selection = 30 days (the longest, used to
    // be the only choice). Triggers the same backend allowlist.
    const [boostChoice, setBoostChoice] = useState(30);
    const triggerBoost = async (days = boostChoice) => {
        setBoostBusy(true);
        try {
            const { data } = await api.post(`/listings/${id}/boost?days=${days}`);
            setListing((l) => ({ ...l, boosted_until: data.boosted_until }));
            toast.success(`Boost activated for ${days} days`);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBoostBusy(false); }
    };

    const reloadListing = async () => {
        try {
            const { data } = await api.get(`/listings/${id}`);
            setListing(data);
        } catch { /* noop */ }
    };

    const handleDelete = async () => {
        try {
            await api.delete(`/listings/${id}`);
            toast.success("Listing deleted");
            navigate("/my-listings");
        } catch (e) { toast.error(formatApiError(e)); }
    };

    useEffect(() => {
        api.get(`/listings/${id}`)
            .then((r) => {
                // v77.1 — Affiliate listings (Sponsored Ads) must NEVER
                // render the Adukoo intermediate detail page. Whether the
                // visitor arrived via a shared link, a bookmark, an SEO
                // result or pasted-URL, we send them straight to the
                // partner site. Click-count beacon still fires so admins
                // see the referral conversion. window.location.replace()
                // so the user can't accidentally hit Back into the empty
                // intermediate page.
                const data = r.data;
                if (data?.is_affiliate && data?.affiliate_url) {
                    try { api.post(`/listings/${id}/affiliate-click`).catch(() => {}); } catch { /* ignore */ }
                    window.location.replace(data.affiliate_url);
                    return;
                }
                setListing(data);
                if (data?.title) {
                    document.title = `${data.title} — Adukoo`;
                }
                // fire view-track only after a successful fetch and only for non-owners
                if (data && (!user || user.id !== data.user_id)) {
                    trackView(id);
                }
                // AdMob interstitial — every Nth detail-view on the Android app
                // only (no-op on web). Won't block the page; ignores failures.
                maybeShowInterstitial();
            })
            .catch(() => setError("Listing not found"));
    }, [id, user]);

    // v78.32 — Per-listing SEO + Product JSON-LD. Without this the
    // SPA inherits the homepage's canonical / OG tags and Google
    // consolidates ranking to `/` — see SEO audit Feb 2026.
    const seoOpts = (() => {
        if (!listing) return { title: "Listing — Adukoo" };
        const titleStr = `${listing.title} — Adukoo`;
        const baseDesc = (listing.description || "").replace(/\s+/g, " ").trim();
        const desc = baseDesc.length > 160 ? baseDesc.slice(0, 157) + "..." : (baseDesc || `${listing.title} on Adukoo${listing.city ? ` — ${listing.city}` : ""}`);
        const canonical = `${SEO_BASE_URL}/listing/${listing.id}`;
        // v78.49 — Merchant-listings structured-data fixes per Google
        // Search Console report (Feb 2026):
        //   1. Invalid URL in "image" — images coming back as relative
        //      `/api/img/...` paths fail Google's URL validator. Coerce
        //      every image to an absolute https URL rooted at SEO_BASE_URL.
        //   2. No global identifier (gtin, brand) — fall back to the
        //      seller's shop name as Brand when the listing's explicit
        //      brand field is empty. Real classifieds rarely carry a GTIN
        //      so brand-fallback is the cleanest legal signal.
        //   3. Missing shippingDetails in offers — add an explicit
        //      OfferShippingDetails block. Adukoo doesn't ship itself, so
        //      we declare zero shipping cost + in-person/seller-arranged
        //      delivery scope. This satisfies Google's check without
        //      making promises sellers cannot keep.
        //   4. Missing hasMerchantReturnPolicy in offers — add a
        //      MerchantReturnPolicy block reflecting the platform reality:
        //      Adukoo is a marketplace; returns are negotiated
        //      directly with the seller. We declare "NoReturns" as the
        //      platform default with a 0-day window; Verified-Business
        //      sellers can override this later via the Pro dashboard.
        const absImage = (u) => {
            if (!u) return undefined;
            if (/^https?:\/\//i.test(u)) return u;
            if (u.startsWith("/")) return `${SEO_BASE_URL}${u}`;
            return `${SEO_BASE_URL}/${u}`;
        };
        const absImages = (listing.images || []).slice(0, 5).map(absImage).filter(Boolean);
        const ogImage = absImages[0] || `${SEO_BASE_URL}/adukoo-og.jpg`;
        const brandName = listing.brand || listing.seller_shop_name || listing.seller_name || "Adukoo";
        const isJob = listing.category === "jobs";
        const jsonLd = isJob
            ? {
                "@context": "https://schema.org",
                "@type": "JobPosting",
                "title": listing.title,
                "description": baseDesc || listing.title,
                "datePosted": listing.created_at,
                "employmentType": (listing.job_meta?.employment_type || "FULL_TIME").toUpperCase(),
                "hiringOrganization": {
                    "@type": "Organization",
                    "name": listing.seller_name || "Adukoo Employer",
                    "sameAs": canonical,
                },
                "jobLocation": {
                    "@type": "Place",
                    "address": {
                        "@type": "PostalAddress",
                        "addressLocality": listing.city || undefined,
                        "addressRegion": listing.state || undefined,
                        "postalCode": listing.pincode || undefined,
                        "addressCountry": listing.country || undefined,
                    },
                },
                "validThrough": listing.expires_at || undefined,
            }
            : {
                "@context": "https://schema.org",
                "@type": "Product",
                "name": listing.title,
                "description": baseDesc || listing.title,
                "image": absImages.length ? absImages : [`${SEO_BASE_URL}/adukoo-og.jpg`],
                "category": listing.category,
                "brand": { "@type": "Brand", "name": brandName },
                "offers": typeof listing.price === "number" ? {
                    "@type": "Offer",
                    "url": canonical,
                    "priceCurrency": listing.currency || "INR",
                    "price": listing.price,
                    "availability": listing.status === "active" ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
                    "itemCondition": listing.condition === "new" ? "https://schema.org/NewCondition" : "https://schema.org/UsedCondition",
                    "seller": { "@type": "Person", "name": listing.seller_name || "Adukoo seller" },
                    "shippingDetails": {
                        "@type": "OfferShippingDetails",
                        "shippingRate": {
                            "@type": "MonetaryAmount",
                            "value": 0,
                            "currency": listing.currency || "INR",
                        },
                        "shippingDestination": {
                            "@type": "DefinedRegion",
                            "addressCountry": listing.country || "IN",
                        },
                        "deliveryTime": {
                            "@type": "ShippingDeliveryTime",
                            "handlingTime": { "@type": "QuantitativeValue", "minValue": 0, "maxValue": 1, "unitCode": "DAY" },
                            "transitTime": { "@type": "QuantitativeValue", "minValue": 0, "maxValue": 7, "unitCode": "DAY" },
                        },
                    },
                    "hasMerchantReturnPolicy": {
                        "@type": "MerchantReturnPolicy",
                        "applicableCountry": listing.country || "IN",
                        "returnPolicyCategory": "https://schema.org/MerchantReturnNotPermitted",
                    },
                } : undefined,
            };
        return { title: titleStr, description: desc, canonical, image: ogImage, type: isJob ? "article" : "product", jsonLd };
    })();
    useSeoMeta(seoOpts);

    // Buyer: load my-own contact-request status to show pill correctly.
    useEffect(() => {
        if (!user || !listing || user.id === listing.user_id) { setContactReq(null); return; }
        api.get(`/listings/${id}/contact-request`)
            .then((r) => setContactReq(r.data))
            .catch(() => setContactReq({ status: "none" }));
    }, [id, user, listing]);

    const requestContact = async () => {
        if (authLoading) return;
        if (!user) {
            // v78.59 — inline modal instead of /login redirect.
            const ok = await requireAuth("Sign in to request seller contact");
            if (!ok) return;
        }
        setContactBusy(true);
        try {
            const { data } = await api.post(`/listings/${id}/contact-request`, {});
            setContactReq(data);
            toast.success(data.status === "approved" ? "Contact already shared with you" : "Request sent — seller will be notified");
            if (data.status === "approved") {
                const fresh = await api.get(`/listings/${id}`);
                setListing(fresh.data);
            }
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setContactBusy(false); }
    };

    const startChat = async () => {
        if (authLoading) return;
        if (!user) {
            // v78.59 — track the intent then open inline modal.
            trackClick(id, "chat");
            const ok = await requireAuth("Sign in to chat with the seller");
            if (!ok) return;
        }
        if (listing.user_id === user.id) { toast.error("This is your own listing"); return; }
        // track interest immediately, before opening chat thread
        trackClick(id, "chat");
        setChatLoading(true);
        try {
            const { data } = await api.post("/chats/start", { listing_id: id });
            navigate(`/messages?thread=${encodeURIComponent(data.id)}`);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setChatLoading(false); }
    };

    const adminRemove = async () => {
        try {
            await api.post(`/admin/listings/${id}/remove`);
            toast.success("Listing removed");
            navigate("/admin");
        } catch (e) { toast.error(formatApiError(e)); }
    };

    // v79.183 — Item 26: Universal Admin Live-Render Edit. State
    // MUST live before the early error / loading returns so the hook
    // order stays constant across renders (rules-of-hooks).
    const [adminEditing, setAdminEditing] = useState(false);
    const [adminDraft, setAdminDraft] = useState({});
    const [adminSaving, setAdminSaving] = useState(false);
    // v79.190 — Founder direction (Feb 2026): "admin can also directly
    // open any ad from any page and edit it. he can have any route.
    // enable this." Any admin visiting a listing detail auto-enters
    // Live Edit mode — no dialog, no extra clicks. Non-admins are
    // unaffected. Admins can hit Cancel to bail out of edit mode
    // (state resets to false, doesn't re-arm on this render pass —
    // scoped by a ref-style flag so we don't fight the user's Cancel).
    const [adminAutoTriggered, setAdminAutoTriggered] = useState(false);
    const wantAdminEdit = searchParams.get("admin_edit") === "1";
    useEffect(() => {
        if (!listing || !user || user.role !== "admin") return;
        if (adminEditing) return;
        if (adminAutoTriggered && !wantAdminEdit) return;
        setAdminDraft({
            title: listing.title || "",
            price: listing.price ?? "",
            mrp: listing.mrp ?? "",
            description: listing.description || "",
            category: listing.category || "",
            brand: listing.brand || "",
            condition: listing.condition || "",
            currency: (listing.currency || "INR").toUpperCase(),
            quantity: listing.quantity ?? "",
            city: listing.city || "",
            state: listing.state || "",
            country: listing.country || "",
            pincode: listing.pincode || "",
            status: listing.status || "active",
            verified: !!listing.verified,
            boosted: !!(listing.boosted_until && new Date(listing.boosted_until) > new Date()),
            website_url: listing.website_url || "",
            seller_name: listing.seller_name || "",
            seller_phone: listing.seller_phone || "",
            seller_email: listing.seller_email || "",
            meta_json: JSON.stringify(listing.meta || {}, null, 2),
            // v79.198 — Photos included so admin can add/remove/reorder inline.
            images: Array.isArray(listing.images) ? listing.images.slice(0) : [],
        });
        setAdminEditing(true);
        setAdminAutoTriggered(true);
        if (wantAdminEdit) {
            toast.info("Admin live-edit mode", { description: "Edit any attribute inline. Hit Save to persist." });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [wantAdminEdit, listing, user]);

    if (error) {
        return (
            <div className="max-w-3xl mx-auto p-10 text-center">
                <div className="font-display text-2xl font-bold text-aduku-navy">{error}</div>
                <Link to="/browse" className="inline-block mt-4 text-aduku-yellow-600 font-semibold">Browse all listings</Link>
            </div>
        );
    }
    if (!listing) return <div className="max-w-3xl mx-auto p-10 text-center text-aduku-navy/60">Loading...</div>;

    const imgs = listing.images?.length ? listing.images : [null];
    const isAdmin = user?.role === "admin";
    const isOwner = user?.id === listing.user_id;

    // Edit-Live helpers — safe to declare here because early returns
    // above never fire once we've reached this point.
    const startAdminEdit = () => {
        setAdminDraft({
            title: listing.title || "",
            price: listing.price ?? "",
            mrp: listing.mrp ?? "",
            description: listing.description || "",
            // v79.191 — Full-power admin edit. Founder direction: "all
            // sections, all information across user posts must be
            // editable — admin has all rights." Expanded draft covers
            // every field the backend AdminListingPatch schema accepts
            // (see server.py L5680+). Sensitive flags (verified,
            // boosted, status) live in the same draft so a single
            // PATCH persists everything atomically.
            category: listing.category || "",
            brand: listing.brand || "",
            condition: listing.condition || "",
            currency: (listing.currency || "INR").toUpperCase(),
            quantity: listing.quantity ?? "",
            city: listing.city || "",
            state: listing.state || "",
            country: listing.country || "",
            pincode: listing.pincode || "",
            status: listing.status || "active",
            verified: !!listing.verified,
            boosted: !!(listing.boosted_until && new Date(listing.boosted_until) > new Date()),
            website_url: listing.website_url || "",
            seller_name: listing.seller_name || "",
            seller_phone: listing.seller_phone || "",
            seller_email: listing.seller_email || "",
            meta_json: JSON.stringify(listing.meta || {}, null, 2),
            // v79.198 — Include images array in the admin draft so
            // the photo manager can add / remove / reorder them and
            // the PATCH carries them through to the backend storage
            // pipeline.
            images: Array.isArray(listing.images) ? listing.images.slice(0) : [],
        });
        setAdminEditing(true);
    };
    const saveAdminEdit = async () => {
        setAdminSaving(true);
        try {
            // v79.191 — Parse meta JSON textarea into a real object.
            // If the textarea is invalid JSON we alert and abort so
            // the admin can fix it — safer than sending a null meta.
            let metaObj;
            try {
                metaObj = adminDraft.meta_json ? JSON.parse(adminDraft.meta_json) : {};
            } catch {
                toast.error("Meta JSON is invalid — fix the braces / quotes and try again.");
                setAdminSaving(false);
                return;
            }
            const payload = {
                title: adminDraft.title,
                description: adminDraft.description,
                price: adminDraft.price === "" ? null : Number(adminDraft.price),
                mrp: adminDraft.mrp === "" ? null : Number(adminDraft.mrp),
                category: adminDraft.category,
                brand: adminDraft.brand || null,
                condition: adminDraft.condition || "",
                currency: (adminDraft.currency || "INR").toUpperCase(),
                quantity: adminDraft.quantity === "" ? null : Number(adminDraft.quantity),
                city: adminDraft.city,
                state: adminDraft.state,
                country: adminDraft.country,
                pincode: adminDraft.pincode,
                status: adminDraft.status,
                verified: !!adminDraft.verified,
                boosted: !!adminDraft.boosted,
                website_url: adminDraft.website_url || "",
                seller_name: adminDraft.seller_name,
                seller_phone: adminDraft.seller_phone,
                seller_email: adminDraft.seller_email,
                meta: metaObj,
                // v79.198 — Photos: pass the current admin-managed
                // array so the backend can persist/rearrange them.
                // Data-URLs added by the picker are stored via the
                // server's `_persist_images_via_storage` pipeline.
                images: Array.isArray(adminDraft.images) ? adminDraft.images : undefined,
            };
            const { data } = await api.patch(`/admin/listings/${listing.id}`, payload);
            const refreshed = data?.listing || data;
            if (refreshed?.id) setListing(refreshed);
            toast.success("Live edit saved");
            setAdminEditing(false);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setAdminSaving(false); }
    };

    return (
        <div
            data-testid="listing-detail-page"
            className="max-w-6xl mx-auto px-6 py-8 relative"
            onTouchStart={onPageSwipeStart}
            onTouchEnd={onPageSwipeEnd}
        >
            {/* v78.10 — Prev / Next ad chevrons. Desktop = side-pinned
                circular buttons; mobile users can also swipe the page
                horizontally to jump (handled by onPageSwipe*). Hidden
                when there's no neighbour in that direction. */}
            {neighbors?.prev?.id && (
                <button
                    type="button"
                    data-testid="listing-prev-btn"
                    onClick={() => goNeighbor("prev")}
                    title={`Previous: ${neighbors.prev.title}`}
                    aria-label="Previous ad"
                    className="hidden md:flex fixed left-3 top-1/2 -translate-y-1/2 z-30 w-11 h-11 rounded-full bg-aduku-navy text-aduku-yellow shadow-lg shadow-aduku-navy/30 border border-aduku-navy-800 items-center justify-center hover:bg-aduku-navy-700 hover:scale-105 active:scale-95 transition-all"
                >
                    <ArrowLeft className="w-5 h-5" strokeWidth={2.5} />
                </button>
            )}
            {neighbors?.next?.id && (
                <button
                    type="button"
                    data-testid="listing-next-btn"
                    onClick={() => goNeighbor("next")}
                    title={`Next: ${neighbors.next.title}`}
                    aria-label="Next ad"
                    className="hidden md:flex fixed right-3 top-1/2 -translate-y-1/2 z-30 w-11 h-11 rounded-full bg-aduku-navy text-aduku-yellow shadow-lg shadow-aduku-navy/30 border border-aduku-navy-800 items-center justify-center hover:bg-aduku-navy-700 hover:scale-105 active:scale-95 transition-all"
                >
                    <ArrowRight className="w-5 h-5" strokeWidth={2.5} />
                </button>
            )}

            <div className="flex items-center justify-between gap-2 mb-4">
                {/* v79.5 — Removed duplicate Back button. Global <BackButton />
                    in App.js renders above this section with the same
                    `navigate(-1)` + safe-history fallback logic. */}
                <div />
                {/* Inline prev/next pills for mobile — chevrons above are
                    hidden on mobile to avoid covering content; users
                    either swipe or tap these. */}
                <div className="md:hidden flex items-center gap-1.5">
                    {neighbors?.prev?.id && (
                        <button
                            type="button"
                            data-testid="listing-prev-pill"
                            onClick={() => goNeighbor("prev")}
                            className="inline-flex items-center gap-1 h-8 px-2.5 rounded-full bg-white border border-aduku-border text-aduku-navy text-xs font-bold active:scale-95"
                        >
                            <ArrowLeft className="w-3.5 h-3.5" /> Prev
                        </button>
                    )}
                    {neighbors?.next?.id && (
                        <button
                            type="button"
                            data-testid="listing-next-pill"
                            onClick={() => goNeighbor("next")}
                            className="inline-flex items-center gap-1 h-8 px-2.5 rounded-full bg-aduku-navy text-aduku-yellow text-xs font-bold active:scale-95"
                        >
                            Next <ArrowRight className="w-3.5 h-3.5" />
                        </button>
                    )}
                </div>
            </div>

            <div className="grid md:grid-cols-[1.4fr_1fr] gap-6 md:gap-8">
                {/* IMAGE GALLERY — top on mobile, left column row 1 on desktop */}
                <div className="min-w-0 md:col-start-1 md:row-start-1">
                    <div className="relative bg-white border border-aduku-border rounded-xl overflow-hidden">
                        <div
                            className="relative aspect-[4/3] bg-white group touch-pan-y select-none overflow-hidden"
                            data-protected="true"
                            onTouchStart={onSwipeStart}
                            onTouchEnd={onSwipeEnd}
                        >
                            {/* v79.181 — Founder direction (Feb 2026):
                                "background space of images is WHITE
                                only if there are gaps". Removed the
                                blurred filler backdrop — letterboxed
                                photos now sit on a clean white canvas
                                like real e-commerce product pages. */}
                            {imgs[activeImg] ? (
                                <>
                                    {listing.category === "job-seeker" ? (
                                        // v77.28 — candidate photos cannot be
                                        // zoomed / opened in lightbox / saved.
                                        // Right-click + drag + long-press are
                                        // all disabled; a diagonal watermark
                                        // marks any screen capture that leaks.
                                        <>
                                            <ProtectedImage
                                                src={imgs[activeImg]}
                                                alt={listing.title}
                                                objectFit="contain"
                                                className="w-full h-full"
                                                testId="listing-cover-img-protected"
                                            />
                                            <div className="absolute top-3 right-3 z-10">
                                                <ReportPhotoDialog listingId={listing.id} compact />
                                            </div>
                                        </>
                                    ) : (
                                        <img
                                            src={imgs[activeImg]}
                                            alt={listing.title}
                                            data-testid="listing-cover-img"
                                            onClick={() => { setLightboxOpen(true); setZoom(1); }}
                                            className="relative w-full h-full object-contain cursor-zoom-in select-none"
                                        />
                                    )}
                                    {listing.category !== "job-seeker" && (
                                        <button
                                            type="button"
                                            onClick={() => { setLightboxOpen(true); setZoom(1); }}
                                            data-testid="listing-zoom-btn"
                                            className="absolute bottom-3 right-3 h-9 w-9 rounded-full bg-aduku-navy/85 text-white hover:bg-aduku-navy flex items-center justify-center shadow-lg"
                                            aria-label="Zoom image"
                                        >
                                            <ZoomIn className="w-4 h-4" />
                                        </button>
                                    )}
                                </>
                            ) : (
                                <div className="w-full h-full flex items-center justify-center text-aduku-navy/30 font-display text-4xl">Adukoo</div>
                            )}
                            {imgs.length > 1 && (
                                <>
                                    <button
                                        type="button"
                                        onClick={() => setActiveImg((i) => (i - 1 + imgs.length) % imgs.length)}
                                        data-testid="listing-prev-img"
                                        className="absolute left-2 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-white/85 hover:bg-white text-aduku-navy flex items-center justify-center shadow"
                                        aria-label="Previous image"
                                    >
                                        <ChevronLeft className="w-5 h-5" />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setActiveImg((i) => (i + 1) % imgs.length)}
                                        data-testid="listing-next-img"
                                        className="absolute right-2 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-white/85 hover:bg-white text-aduku-navy flex items-center justify-center shadow"
                                        aria-label="Next image"
                                    >
                                        <ChevronRight className="w-5 h-5" />
                                    </button>
                                    <div className="absolute bottom-3 left-3 px-2.5 py-0.5 rounded-full bg-aduku-navy/85 text-white text-[11px] font-semibold">
                                        {activeImg + 1} / {imgs.length}
                                    </div>
                                </>
                            )}
                        </div>
                        <div className="absolute top-3 left-3 flex items-center gap-2">
                            {/* v77.20 — condition pills hidden for the
                                NO_CONDITION_CATEGORIES set (Jobs, Open-for-Work,
                                services etc) since "Brand New / Used" makes
                                no sense for a human or a service. */}
                            {shouldShowConditionTag(listing.category) && listing.condition === "new" && (
                                <span className="px-3 py-1 rounded-full bg-aduku-yellow text-aduku-navy text-xs font-bold uppercase tracking-wider">New</span>
                            )}
                            {shouldShowConditionTag(listing.category) && listing.condition === "old" && (
                                <span className="px-3 py-1 rounded-full bg-white border border-aduku-navy text-aduku-navy text-xs font-bold uppercase tracking-wider">Used</span>
                            )}
                            {/* v79.194 — Boosted-status badge removed
                                from the public detail page. Owner still
                                sees the boost state on /my-listings. */}
                        </div>
                    </div>
                    {imgs.length > 1 && (
                        <div className="flex gap-2 mt-3 overflow-x-auto">
                            {imgs.map((im, i) => (
                                <button
                                    key={i}
                                    onClick={() => setActiveImg(i)}
                                    data-testid={`thumb-${i}`}
                                    className={`w-20 h-20 rounded-lg border-2 overflow-hidden shrink-0 ${activeImg === i ? "border-aduku-navy" : "border-transparent"}`}
                                >
                                    <img src={im} alt="" className="w-full h-full object-cover" />
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                {/* DESCRIPTION BLOCK — placed AFTER aside in visual order on mobile (via order-3) so users see price/contact first; on desktop explicit grid puts it under the image (row 2 col 1). min-w-0 prevents long unbreakable strings in the description from forcing the column wider than the viewport on mobile. */}
                <div className="order-3 md:order-none min-w-0 md:col-start-1 md:row-start-2 space-y-6">
                    <div className="bg-white border border-aduku-border rounded-xl p-6">
                        <h2 className="font-display font-bold text-xl text-aduku-navy">Description</h2>
                        <p className="mt-3 text-aduku-navy/80 whitespace-pre-wrap leading-relaxed break-words text-[15px] sm:text-base">{listing.description}</p>
                        {Array.isArray(listing.attachments) && listing.attachments.length > 0 && (
                            <div className="mt-5 pt-4 border-t border-aduku-border">
                                <div className="text-xs uppercase tracking-wider text-aduku-navy/50 font-bold flex items-center gap-1.5"><FileText className="w-3.5 h-3.5" /> Attachments</div>
                                <div className="mt-2 space-y-2" data-testid="listing-attachments">
                                    {listing.attachments.map((a, i) => (
                                        <a
                                            key={i}
                                            href={a.data}
                                            download={a.name || `attachment-${i + 1}`}
                                            data-testid={`attachment-link-${i}`}
                                            className="flex items-center gap-3 px-3 py-2 rounded-lg border border-aduku-border hover:border-aduku-navy hover:bg-aduku-yellow/10 transition group"
                                        >
                                            <div className="w-9 h-9 rounded-lg bg-aduku-navy text-aduku-yellow flex items-center justify-center shrink-0">
                                                <FileText className="w-4 h-4" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="text-sm font-semibold text-aduku-navy truncate">{a.name || `Attachment ${i + 1}`}</div>
                                                <div className="text-[11px] uppercase tracking-wider text-aduku-navy/50">{(a.mime || "").includes("pdf") ? "PDF" : "DOC"} · Download</div>
                                            </div>
                                            <span className="text-aduku-navy/40 text-xs group-hover:text-aduku-navy">↓</span>
                                        </a>
                                    ))}
                                </div>
                            </div>
                        )}
                        {listing.website_url && (
                            <div className="mt-4 pt-4 border-t border-aduku-border">
                                <div className="text-xs uppercase tracking-wider text-aduku-navy/50 font-bold">Also available on the seller&apos;s site</div>
                                <a
                                    data-testid="listing-website-link"
                                    href={listing.website_url}
                                    target="_blank"
                                    rel="nofollow noopener noreferrer"
                                    className="mt-1.5 inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-aduku-bg border border-aduku-border hover:border-aduku-navy text-aduku-navy text-sm font-semibold break-all"
                                    title="Opens in a new tab — Adukoo is not responsible for external sites."
                                >
                                    <img
                                        src={`https://www.google.com/s2/favicons?sz=32&domain=${encodeURIComponent(new URL(listing.website_url).hostname)}`}
                                        alt=""
                                        className="w-4 h-4 shrink-0"
                                        onError={(e) => { e.currentTarget.style.display = "none"; }}
                                    />
                                    {(() => { try { return new URL(listing.website_url).hostname; } catch { return listing.website_url; } })()}
                                    <span className="text-aduku-navy/40 text-xs ml-1">↗</span>
                                </a>
                            </div>
                        )}
                    </div>

                    <ServicesMetaDisplay listing={listing} />
                    <ProductMetaExtrasDisplay listing={listing} />

                    {/* v78.45 — AdSense slot between the Description /
                        Services / Product-meta blocks and the related
                        listings further down. ListingDetail has the
                        highest engagement of any page (~95s avg dwell
                        per analytics), so an unobtrusive contextual ad
                        here monetizes the strongest part of the funnel
                        without interrupting the price/contact flow at
                        the top. Returns null when no slot ID configured. */}
                    <AdSenseSlot
                        slot={process.env.REACT_APP_ADSENSE_SLOT_LISTING_INREAD || ""}
                        testId="adsense-listing-inread"
                        minHeightClass="min-h-[100px] sm:min-h-[200px]"
                    />
                </div>

                {/* ASIDE — right column spanning both rows on desktop; SECOND on mobile (order-2) so the price + contact buttons sit immediately under the image. */}
                <aside className="order-2 md:order-none min-w-0 md:col-start-2 md:row-start-1 md:row-span-2 space-y-4" data-testid="listing-aside">
                    <div className="bg-white border border-aduku-border rounded-xl p-6">
                        <div className="text-xs uppercase tracking-wider text-aduku-navy/50 font-semibold">{listing.category}</div>
                        {/* v79.183 — Item 26: Admin "Edit Live" toggle
                            + inline editable title. Non-admin visitors
                            see the plain title exactly as before. */}
                        {isAdmin && (
                            <div className="mt-2 mb-2 flex items-center gap-2 flex-wrap">
                                {!adminEditing ? (
                                    <button
                                        type="button"
                                        data-testid="admin-edit-live-btn"
                                        onClick={startAdminEdit}
                                        className="inline-flex items-center gap-1 h-7 px-3 rounded-full bg-aduku-navy text-aduku-yellow text-[11px] font-black uppercase tracking-wider hover:brightness-110"
                                        title="Edit this listing in place — admin override"
                                    >
                                        ✏️ Edit Live
                                    </button>
                                ) : (
                                    <>
                                        <button
                                            type="button"
                                            data-testid="admin-save-live-btn"
                                            onClick={saveAdminEdit}
                                            disabled={adminSaving}
                                            className="inline-flex items-center gap-1 h-7 px-3 rounded-full bg-emerald-600 text-white text-[11px] font-black uppercase tracking-wider disabled:opacity-50"
                                        >
                                            {adminSaving ? "Saving…" : "Save"}
                                        </button>
                                        <button
                                            type="button"
                                            data-testid="admin-cancel-live-btn"
                                            onClick={() => setAdminEditing(false)}
                                            className="inline-flex items-center gap-1 h-7 px-3 rounded-full bg-white border border-aduku-navy/25 text-aduku-navy text-[11px] font-bold uppercase tracking-wider"
                                        >
                                            Cancel
                                        </button>
                                        <span className="text-[10px] font-bold uppercase tracking-wider text-aduku-navy/55">Admin override — all users see the change immediately.</span>
                                    </>
                                )}
                            </div>
                        )}
                        {adminEditing ? (
                            <input
                                data-testid="admin-edit-title"
                                value={adminDraft.title}
                                onChange={(e) => setAdminDraft((d) => ({ ...d, title: e.target.value }))}
                                className="w-full mt-1 font-display font-black text-2xl sm:text-3xl text-aduku-navy bg-aduku-yellow/10 rounded-md px-2 py-1 border border-aduku-yellow outline-none"
                            />
                        ) : (
                            <h1 data-testid="listing-title" className="font-display font-black text-2xl sm:text-3xl text-aduku-navy mt-1 break-words">{listing.title}</h1>
                        )}
                        {/* v79.192 — Universal Admin Edit panel — extracted
                            to /components/admin/AdminInlineEditor.jsx to
                            keep this page under a sane line-count. */}
                        {isAdmin && adminEditing && (
                            <AdminInlineEditor draft={adminDraft} setDraft={setAdminDraft} />
                        )}
                        {/* v79.173 — Skip the price/compensation row when
                            the job has no salary band (founder direction:
                            "no need to say Compensation discussed on
                            shortlist. Ignore it if salary offer is not
                            mentioned"). Non-job categories always show
                            the row so a ₹0 product still prints as
                            "Free / Price on request". */}
                        {(() => {
                            const isJobLike = listing.category === "jobs" || listing.category === "job-seeker";
                            // v79.197 — Founder direction (Feb 2026):
                            // Matrimony / Insurance / Mutual-fund /
                            // Movies-OTT ads must never render a price
                            // block on the public detail page — those
                            // categories aren't priced items. The old
                            // "Contact for price" fallback felt
                            // insulting on matrimony ("valuing a
                            // person"), and confusing on the others.
                            const NO_PRICE_DETAIL = new Set([
                                "matrimony", "insurance", "insurance-distributor",
                                "mutual-fund", "mutual-fund-distributor",
                                "movies-ott",
                            ]);
                            if (NO_PRICE_DETAIL.has(listing.category)) return null;
                            if (isJobLike) {
                                const comp = formatCompensationRange(listing.price, listing.meta?.price_max, listing.currency, listing.meta?.compensation_period, listing.category);
                                if (!comp) return null;
                                return (
                                    <div data-testid="listing-price" className="mt-3 font-display font-black text-3xl text-aduku-navy flex items-baseline gap-2 flex-wrap">
                                        <span data-testid="listing-compensation">{comp}</span>
                                    </div>
                                );
                            }
                            return (
                        <div data-testid="listing-price" className="mt-3 font-display font-black text-3xl text-aduku-navy flex items-baseline gap-2 flex-wrap">
                            {listing.is_affiliate && !(Number(listing.price) > 0)
                                ? <span className="text-aduku-navy/60 text-xl">See price on partner site →</span>
                                : formatPrice(listing.price, listing.currency)}
                            {Number(listing.price) > 0 && (
                                <span className="text-xs font-semibold text-aduku-navy/50 uppercase tracking-wider">{listing.currency || "INR"}</span>
                            )}
                            {/* v79.93 — MRP / List Price strike-through
                                anchor with a green discount pill. Label
                                (MRP vs "List") is driven by category
                                bucket via `mrpShortLabel`. */}
                            {discountPercent(listing.mrp, listing.price) > 0 && (
                                <span data-testid="listing-mrp-block" className="flex items-baseline gap-1.5">
                                    <span className="text-sm font-semibold text-aduku-navy/45 line-through">
                                        {mrpShortLabel(listing.category)} {formatPrice(listing.mrp, listing.currency)}
                                    </span>
                                    <span data-testid="listing-discount" className="text-[11px] font-black px-2 py-0.5 rounded-full bg-emerald-500 text-white tracking-wider uppercase">
                                        {discountPercent(listing.mrp, listing.price)}% OFF
                                    </span>
                                </span>
                            )}
                        </div>
                            );
                        })()}

                        <div className="mt-5 space-y-2 text-sm text-aduku-navy/80">
                            <div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-aduku-navy/50" /> {listing.city}, {listing.state} - {listing.pincode}</div>
                            <div className="flex items-center gap-2"><Calendar className="w-4 h-4 text-aduku-navy/50" /> Posted {new Date(listing.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</div>
                            {shouldShowConditionTag(listing.category) && listing.condition && (listing.condition === "new" || listing.condition === "old") && (
                                <div className="flex items-center gap-2"><Tag className="w-4 h-4 text-aduku-navy/50" /> Condition: <b className="capitalize">{listing.condition === "new" ? "Brand New" : "Used"}</b></div>
                            )}
                            {listing.brand && listing.category !== "jobs" && listing.category !== "job-seeker" && (
                                <div className="flex items-center gap-2" data-testid="listing-brand">
                                    <Tag className="w-4 h-4 text-aduku-navy/50" /> Brand: <b>{listing.brand}</b>
                                </div>
                            )}
                            {(() => {
                                // v79.111 — Do NOT show a "Stock" row when either:
                                //   1) the listing belongs to a SERVICE-type category
                                //      (services aren't inventoried in units), OR
                                //   2) the effective stock is 0 AND no explicit
                                //      quantity was ever set (i.e., stock isn't a
                                //      meaningful concept for this ad).
                                // Otherwise treat like a physical goods listing.
                                const SERVICE_LIKE = new Set([
                                    "event-management", "groceries", "fruits-vegetables",
                                    "home-maid", "mutual-fund-distributor",
                                    "insurance-distributor", "services", "it-software",
                                    "jobs", "job-seeker", "matrimony", "real-estate",
                                    "real-estate-rental", "vehicle-rental",
                                    "restaurants", "function-halls",
                                    "beauty-parlour", "salon", "tuition",
                                    "photography", "electrician", "plumber",
                                    "carpenter", "packers-movers", "car-rental",
                                    "bike-rental", "loans", "tax-consultant",
                                    "legal", "medical", "hospitals",
                                ]);
                                if (SERVICE_LIKE.has(listing.category)) return null;
                                const remaining = Math.max(0, (listing.quantity || 0) - (listing.sold_quantity || 0));
                                const listedQty = listing.quantity || 0;
                                if (remaining === 0 && listedQty === 0) return null;
                                return (
                                    <div className="flex items-center gap-2">
                                        <Tag className="w-4 h-4 text-aduku-navy/50" /> Stock:
                                        <b data-testid="listing-stock">{remaining}</b>
                                        {listedQty > 0 && <span className="text-aduku-navy/50 text-xs">of {listedQty} listed</span>}
                                        {listing.status === "sold_out" && (
                                            <span className="ml-2 px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-[10px] font-bold uppercase">Sold out</span>
                                        )}
                                    </div>
                                );
                            })()}
                            {listing.cv_file && (
                                <a
                                    data-testid="download-cv-link"
                                    href={listing.cv_file}
                                    download={listing.cv_filename || "cv.pdf"}
                                    className="inline-flex items-center gap-2 px-3 py-1.5 mt-2 rounded-full bg-aduku-yellow/30 text-aduku-navy text-xs font-bold border border-aduku-yellow/60 hover:bg-aduku-yellow/50"
                                >
                                    <FileText className="w-3.5 h-3.5" /> Download CV ({listing.cv_filename || "cv.pdf"})
                                </a>
                            )}
                            {/* v78.2 — When the viewer ISN'T an employer-verified
                                user (and isn't the candidate themselves), the
                                backend `strip_job_seeker_contact` masks the
                                resume + expected salary. Surface a clear
                                "Verify as employer" CTA so recruiters know
                                exactly how to unlock CV & expected pay. */}
                            {listing.category === "job-seeker" &&
                                listing.viewer_can_see_candidate_contact === false && (
                                    <a
                                        href="/employer/apply"
                                        data-testid="verify-employer-cta"
                                        className="mt-2 flex items-center gap-2 px-3 py-2 rounded-lg bg-aduku-navy text-white text-xs font-bold hover:bg-aduku-navy/90 transition"
                                    >
                                        <Shield className="w-3.5 h-3.5 text-aduku-yellow" />
                                        <span className="flex-1 leading-snug">
                                            Resume & expected salary are private. <span className="underline">Verify as employer to view →</span>
                                        </span>
                                    </a>
                                )}
                            {/* v77.13 — Job-Seeker meta display (skills /
                                years / open-to). Always visible on the
                                ad — the gate only hides resume + contact,
                                not the skill profile. v77.23 — show
                                experience as "X yrs Y mos" (was just yrs).
                                v77.28 — also surface the at-your-own-risk
                                / no-verification disclaimer to viewers so
                                recruiters know Adukoo doesn't vouch for
                                anything the candidate has typed in. */}
                            {listing.category === "job-seeker" && (
                                <div className="mt-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-[11px] text-amber-900 leading-snug" data-testid="job-seeker-viewer-disclaimer">
                                    <b>Disclaimer:</b> Adukoo does not verify candidate claims, photos or resumes. The information on this listing is published by the candidate under their own responsibility. Please conduct your own background checks before any hiring decision. Adukoo is a listing platform only and accepts no liability for any outcome.
                                </div>
                            )}
                            {listing.category === "job-seeker" && listing.meta && (
                                <div data-testid="job-seeker-profile" className="mt-3 space-y-2">
                                    {(listing.meta.years_of_experience != null || listing.meta.months_of_experience != null) && (
                                        <div className="flex items-center gap-2"><Tag className="w-4 h-4 text-aduku-navy/50" /> Experience: <b>
                                            {(() => {
                                                const y = Number(listing.meta.years_of_experience) || 0;
                                                const m = Number(listing.meta.months_of_experience) || 0;
                                                if (!y && !m) return "Fresher";
                                                const parts = [];
                                                if (y) parts.push(`${y} yr${y === 1 ? "" : "s"}`);
                                                if (m) parts.push(`${m} mo${m === 1 ? "" : "s"}`);
                                                return parts.join(" ");
                                            })()}
                                        </b></div>
                                    )}
                                    {Array.isArray(listing.meta.skills) && listing.meta.skills.length > 0 && (
                                        <div>
                                            <div className="text-[11px] uppercase tracking-wider text-aduku-navy/55 font-bold">Skills</div>
                                            <div className="mt-1 flex flex-wrap gap-1">
                                                {listing.meta.skills.map((s, i) => (
                                                    <span key={i} className="px-2 py-0.5 rounded-full bg-aduku-bg text-aduku-navy text-[11px] font-bold uppercase tracking-wider border border-aduku-border">{s}</span>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    {Array.isArray(listing.meta.open_to_locations) && listing.meta.open_to_locations.length > 0 && (
                                        <div className="text-[12px] text-aduku-navy/75">Open to: <b>{listing.meta.open_to_locations.join(" · ")}</b></div>
                                    )}
                                    {listing.meta.current_company && (
                                        <div className="text-[12px] text-aduku-navy/75 flex items-center gap-1.5"><Tag className="w-3.5 h-3.5 text-aduku-navy/50" /> Currently at: <b>{listing.meta.current_company}</b></div>
                                    )}
                                    {listing.meta.open_to_relocate && (
                                        <div className="inline-block px-2 py-0.5 rounded-full bg-aduku-yellow/40 text-aduku-navy text-[11px] font-bold">Open to relocate</div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="bg-aduku-navy text-white rounded-xl p-6">
                        <div className="text-xs text-aduku-yellow uppercase tracking-wider font-semibold flex items-center gap-1.5">
                            {(() => {
                                // v79.173 — Founder direction: "job posting
                                // person cannot be termed Seller. Just say
                                // Posted by, jobs category, matrimony, all
                                // applicable categories."
                                // Categories where the poster is NOT a
                                // merchant get a neutral "Posted by" label
                                // (or a category-specific one like
                                // "Profile owner"). Commerce categories
                                // keep the "Seller" / "Business Seller"
                                // labelling.
                                const NON_MERCHANT = new Set([
                                    "jobs", "job-seeker", "job-profiles",
                                    "matrimony",
                                    "roommates", "flatmates",
                                    "lost-and-found",
                                    "community", "events",
                                    "volunteers", "adoption",
                                ]);
                                if (listing.category === "job-seeker") {
                                    return <><UserIcon className="w-3 h-3" /> Profile owner</>;
                                }
                                if (NON_MERCHANT.has(listing.category)) {
                                    return <><UserIcon className="w-3 h-3" /> Posted by</>;
                                }
                                if (listing.seller_account_type === "business") {
                                    return <><Shield className="w-3 h-3" /> Business Seller</>;
                                }
                                return "Seller";
                            })()}
                        </div>
                        <div className="font-display font-bold text-xl mt-1 flex items-center gap-2" data-testid="seller-name">
                            {listing.seller_name}
                            {listing.seller_verified && <VerifiedBadge variant="corner" />}
                        </div>
                        {listing.seller_account_type === "business" && listing.seller_gstin && (
                            <div className="text-[11px] text-white/60 mt-0.5 font-mono tracking-wider">GSTIN: {listing.seller_gstin}</div>
                        )}
                        {/* v78.9 — Subscribe to this seller. Hidden on own
                            listings (SubscribeButton ALSO short-circuits on
                            is_self via server response). Pill-light variant
                            sits well on the navy seller card. */}
                        {listing.user_id && !isOwner && (
                            <div className="mt-3">
                                <SubscribeButton
                                    userId={listing.user_id}
                                    isSelf={user?.id === listing.user_id}
                                    variant="pill-light"
                                    showCount
                                    testId="listing-seller-subscribe-btn"
                                />
                            </div>
                        )}
                        <div className="mt-5 space-y-2">
                            {/* v77.13 — Job-Seeker viewer gate. If the
                                listing is Open-for-Work and the viewer
                                isn't a verified employer (and isn't the
                                candidate themselves), we replace the
                                normal Chat/Call/Email cluster with a
                                single "Verify as Employer" CTA. */}
                            {listing.category === "job-seeker" && !isOwner && !listing.viewer_can_see_candidate_contact && (
                                <div data-testid="js-employer-gate" className="rounded-xl bg-aduku-yellow/10 border border-aduku-yellow/50 p-4">
                                    <div className="flex items-start gap-2.5">
                                        <ShieldCheck className="w-5 h-5 text-aduku-yellow mt-0.5 shrink-0" />
                                        <div className="min-w-0">
                                            <div className="font-bold text-white text-sm">Verify as Employer to unlock</div>
                                            <div className="text-[12px] text-white/80 mt-1 leading-relaxed">
                            Resume download, phone &amp; email are only visible to verified employers. One-time application, ~24 hr review.
                                            </div>
                                            <button
                                                type="button"
                                                data-testid="js-verify-employer-cta"
                                                onClick={() => {
                                                    if (!user) navigate(`/login?redirect=/employer/apply`);
                                                    else navigate("/employer/apply");
                                                }}
                                                className="mt-3 w-full aduku-yellow-btn hover:bg-aduku-yellow-600 h-10 rounded-full font-bold text-sm"
                                            >
                                                Verify as Employer →
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                            {listing.category === "jobs" && !isOwner && (
                                <>
                                    <JobApplyDialog listing={listing} />
                                    {listing.meta?.job_link && (
                                        <a
                                            data-testid="job-link-btn"
                                            href={listing.meta.job_link}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="w-full h-11 rounded-full flex items-center justify-center gap-2 font-semibold bg-white/10 hover:bg-white/20 text-white border border-white/20"
                                        >
                                            <FileText className="w-4 h-4" /> View full posting
                                        </a>
                                    )}
                                </>
                            )}
                            {listing.category !== "jobs" && !(listing.category === "job-seeker" && !isOwner && !listing.viewer_can_see_candidate_contact) && !isOwner && (
                                <button
                                    data-testid="chat-seller-btn"
                                    onClick={startChat}
                                    disabled={chatLoading}
                                    className="w-full aduku-yellow-btn h-11 rounded-full flex items-center justify-center gap-2 disabled:opacity-60"
                                >
                                    <MessageSquare className="w-4 h-4" /> {(() => {
                                        // v79.173 — Contextual chat label. Non-
                                        // commerce categories drop the "Seller"
                                        // word (see the Posted-by header block
                                        // above for the same taxonomy).
                                        if (chatLoading) return "Opening chat...";
                                        const c = listing.category;
                                        if (c === "matrimony") return "Message this person";
                                        if (c === "roommates" || c === "flatmates" || c === "community" || c === "events" || c === "volunteers" || c === "adoption" || c === "lost-and-found") {
                                            return "Message poster";
                                        }
                                        return "Chat with Seller";
                                    })()}
                                </button>
                            )}
                            {/* v79.93 — WhatsApp direct-chat CTA. Only
                                surfaces when the seller enabled the
                                toggle in PostAd AND the listing has an
                                (unmasked) mobile on file. Opens wa.me
                                in a new tab; on mobile the OS routes it
                                to the WhatsApp app. Never rendered on
                                sponsored ads or on the owner's own
                                listing (owners don't chat themselves). */}
                            {!isOwner && listing.whatsapp_enabled && (listing.whatsapp_phone || listing.seller_phone) && !listing.is_affiliate && (
                                <a
                                    data-testid="whatsapp-seller-btn"
                                    href={`https://wa.me/${(listing.whatsapp_phone || listing.seller_phone || "").replace(/[^\d]/g, "")}?text=${encodeURIComponent(`Hi ${listing.seller_name || ""}, I'm interested in your Adukoo listing: ${listing.title}`)}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={() => { try { api.post(`/listings/${id}/track-click`, { kind: "whatsapp" }).catch(() => {}); } catch { /* ignore */ } }}
                                    className="w-full h-11 rounded-full flex items-center justify-center gap-2 font-semibold bg-emerald-500 hover:bg-emerald-600 text-white shadow-sm"
                                >
                                    <MessageSquare className="w-4 h-4" /> Chat on WhatsApp
                                </a>
                            )}
                            <a
                                data-testid="call-seller-btn"
                                href={listing.contact_visible && listing.seller_phone ? `tel:${listing.seller_phone}` : undefined}
                                onClick={(e) => {
                                    if (!listing.contact_visible) { e.preventDefault(); requestContact(); return; }
                                    if (!isOwner) trackClick(id, "call");
                                }}
                                className={`w-full h-11 rounded-full flex items-center justify-center gap-2 font-semibold ${(listing.category === "jobs" || (listing.category === "job-seeker" && !isOwner && !listing.viewer_can_see_candidate_contact)) ? "hidden" : ""} ${listing.contact_visible
                                    ? "bg-white/10 hover:bg-white/20 text-white"
                                    : "bg-white/5 hover:bg-white/10 text-white/80 border border-dashed border-white/30 cursor-pointer"
                                    }`}
                            >
                                <Phone className="w-4 h-4" />
                                {listing.contact_visible
                                    ? `Call ${listing.seller_phone}`
                                    : `Call ${listing.seller_phone_masked || "•••••••• "}`}
                            </a>
                            <a
                                data-testid="email-seller-btn"
                                href={listing.contact_visible && listing.seller_email ? `mailto:${listing.seller_email}?subject=Adukoo: ${encodeURIComponent(listing.title)}` : undefined}
                                onClick={(e) => {
                                    if (!listing.contact_visible) { e.preventDefault(); requestContact(); return; }
                                    if (!isOwner) trackClick(id, "email");
                                }}
                                title={listing.contact_visible && listing.seller_email ? `Click to email · right-click to copy · ${listing.seller_email}` : undefined}
                                className={`w-full min-h-11 py-2 px-4 rounded-full flex items-center justify-center gap-2 font-semibold border border-white/15 select-text break-all text-center ${(listing.category === "jobs" || (listing.category === "job-seeker" && !isOwner && !listing.viewer_can_see_candidate_contact)) ? "hidden" : ""} ${listing.contact_visible
                                    ? "bg-white/5 hover:bg-white/15 text-white"
                                    : "bg-white/5 hover:bg-white/10 text-white/80 border-dashed border-white/30 cursor-pointer"
                                    }`}
                            >
                                <Mail className="w-4 h-4 shrink-0" />
                                <span className="break-all">
                                    {listing.contact_visible
                                        ? (listing.seller_email || "Email Seller")
                                        : `Email ${listing.seller_email_masked || "seller"}`}
                                </span>
                            </a>
                            {!isOwner && !listing.contact_visible && listing.category !== "jobs" && listing.category !== "job-seeker" && (
                                <button
                                    data-testid="request-contact-btn"
                                    onClick={requestContact}
                                    disabled={contactBusy || (contactReq && contactReq.status === "pending")}
                                    className={`w-full h-11 rounded-full flex items-center justify-center gap-2 font-semibold transition-colors ${contactReq && contactReq.status === "denied"
                                        ? "bg-red-500/20 text-red-200 border border-red-400/40"
                                        : contactReq && contactReq.status === "pending"
                                            ? "bg-aduku-yellow/30 text-aduku-yellow border border-aduku-yellow/50 cursor-not-allowed"
                                            : "aduku-yellow-btn hover:bg-aduku-yellow-600 text-aduku-navy"}`}
                                >
                                    <ShieldCheck className="w-4 h-4" />
                                    {contactBusy
                                        ? "Sending..."
                                        : contactReq && contactReq.status === "pending"
                                            ? "Request sent · awaiting seller"
                                            : contactReq && contactReq.status === "denied"
                                                ? "Seller declined — try Chat"
                                                : "Request contact details"}
                                </button>
                            )}
                            {!isOwner && (
                                <WishlistButton listingId={listing.id} ownerId={listing.user_id} size="lg" category={listing.category} />
                            )}
                            <ShareButton path={`/listing/${listing.id}`} title={listing.title} price={listing.price} variant="lg" listingId={listing.id} listingImage={listing.images?.[0]} />
                        </div>
                        <div className="mt-4 text-[11px] text-white/50">Always meet in a public place. Verify before paying.</div>
                        <a
                            data-testid="report-listing-btn"
                            href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(`Report listing: ${listing.title}`)}&body=${encodeURIComponent(`Listing ID: ${listing.id}\nReason: `)}`}
                            className="mt-3 inline-flex items-center gap-1.5 text-[11px] text-white/60 hover:text-aduku-yellow underline underline-offset-2"
                        >
                            <Flag className="w-3 h-3" /> Report this listing
                        </a>
                    </div>

                    {/* DIGITAL STORE CARD — appears only when the listing is
                        attached to one of the seller's Digital Stores. Lets
                        buyers (a) jump to the full storefront catalogue and
                        (b) navigate to the physical address via the device's
                        default Maps app when a location is on file. */}
                    {listing.shop && listing.shop.shop_slug && (
                        <div data-testid="listing-shop-card" className="bg-white border border-aduku-border rounded-xl p-5">
                            <div className="flex items-start gap-3">
                                <div className="w-11 h-11 rounded-xl bg-aduku-navy text-aduku-yellow flex items-center justify-center shrink-0">
                                    <Store className="w-5 h-5" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="text-[11px] uppercase tracking-wider text-aduku-navy/55 font-bold flex items-center gap-1">
                                        Digital Store
                                        {listing.shop.verified && <BadgeCheck className="w-3 h-3 text-aduku-yellow fill-aduku-yellow" />}
                                    </div>
                                    <div data-testid="listing-shop-name" className="font-display font-black text-aduku-navy text-lg leading-tight break-words mt-0.5">
                                        {listing.shop.shop_name}
                                    </div>
                                    {(listing.shop.listing_count || listing.shop.business_type) && (
                                        <div className="text-[12px] text-aduku-navy/60 mt-0.5">
                                            {listing.shop.business_type ? <span className="capitalize">{listing.shop.business_type.replace(/_/g, " ")}</span> : null}
                                            {listing.shop.business_type && listing.shop.listing_count ? " · " : ""}
                                            {listing.shop.listing_count ? `${listing.shop.listing_count} listing${listing.shop.listing_count === 1 ? "" : "s"}` : ""}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {(() => {
                                const s = listing.shop;
                                const addrParts = [s.shop_address, s.shop_city, s.shop_state, s.shop_pincode, s.shop_country].filter(Boolean);
                                const addr = addrParts.join(", ");
                                const hasCoords = typeof s.shop_lat === "number" && typeof s.shop_lng === "number" && (s.shop_lat || s.shop_lng);
                                const hasPhysical = Boolean(addr) || hasCoords;
                                // Default Maps app intent — `geo:` opens
                                // Google Maps on Android, Apple Maps on iOS,
                                // and falls back to a https Google Maps URL
                                // on desktop browsers via the universal link.
                                const mapsUrl = hasCoords
                                    ? `https://www.google.com/maps/search/?api=1&query=${s.shop_lat},${s.shop_lng}`
                                    : addr
                                        ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addr)}`
                                        : null;
                                return (
                                    <>
                                        {addr && (
                                            <div data-testid="listing-shop-address" className="mt-3 flex items-start gap-2 text-aduku-navy/75 text-[13px] leading-snug">
                                                <MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0 text-aduku-navy/55" />
                                                <span className="break-words">{addr}</span>
                                            </div>
                                        )}
                                        <div className="mt-3 grid grid-cols-2 gap-2">
                                            <Link
                                                to={`/digital-stores/${s.shop_slug}`}
                                                data-testid="visit-store-btn"
                                                onClick={() => { if (!isOwner) trackClick(id, "visit-store"); }}
                                                className="h-10 px-3 rounded-full aduku-yellow-btn hover:bg-aduku-yellow-600 text-aduku-navy font-bold text-sm inline-flex items-center justify-center gap-1.5"
                                            >
                                                <Store className="w-4 h-4" /> Visit Digital Store
                                            </Link>
                                            {hasPhysical && mapsUrl ? (
                                                <a
                                                    data-testid="open-maps-btn"
                                                    href={mapsUrl}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    onClick={() => { if (!isOwner) trackClick(id, "directions"); }}
                                                    className="h-10 px-3 rounded-full bg-aduku-navy text-white hover:bg-aduku-navy-700 font-semibold text-sm inline-flex items-center justify-center gap-1.5"
                                                >
                                                    <Navigation className="w-4 h-4" /> Get directions
                                                </a>
                                            ) : (
                                                <div className="h-10 px-3 rounded-full bg-aduku-bg/60 text-aduku-navy/40 font-semibold text-xs inline-flex items-center justify-center gap-1.5 border border-dashed border-aduku-border" title="This store hasn't shared a physical address">
                                                    <Navigation className="w-3.5 h-3.5" /> Online-only store
                                                </div>
                                            )}
                                        </div>
                                    </>
                                );
                            })()}
                        </div>
                    )}

                    {isOwner && (
                        <div className="bg-white border border-aduku-border rounded-xl p-5">
                            <div className="text-xs uppercase tracking-wider text-aduku-navy/50 font-semibold">Performance</div>
                            <div className="mt-3 grid grid-cols-2 gap-3">
                                <div data-testid="owner-views" className="rounded-xl bg-aduku-bg p-3 flex items-center gap-2">
                                    <Eye className="w-5 h-5 text-aduku-navy/70" />
                                    <div>
                                        <div className="font-display font-black text-aduku-navy text-xl leading-none">{(listing.views || 0).toLocaleString("en-IN")}</div>
                                        <div className="text-[10px] text-aduku-navy/60 uppercase tracking-wider">Views</div>
                                    </div>
                                </div>
                                <div data-testid="owner-clicks" className="rounded-xl bg-aduku-bg p-3 flex items-center gap-2">
                                    <MousePointerClick className="w-5 h-5 text-aduku-navy/70" />
                                    <div>
                                        <div className="font-display font-black text-aduku-navy text-xl leading-none">{(listing.clicks || 0).toLocaleString("en-IN")}</div>
                                        <div className="text-[10px] text-aduku-navy/60 uppercase tracking-wider">Clicks</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {isOwner && (
                        <div className="bg-white border border-aduku-border rounded-xl p-5 space-y-2.5">
                            <div className="text-xs uppercase tracking-wider text-aduku-navy/50 font-semibold">Owner actions</div>
                            <Link
                                to={`/listing/${listing.id}/edit`}
                                data-testid="detail-edit-btn"
                                className="w-full h-11 rounded-full flex items-center justify-center gap-2 bg-aduku-navy text-white hover:bg-aduku-navy-700 font-semibold"
                            >
                                <Pencil className="w-4 h-4" /> Edit listing
                            </Link>
                            <MarkSoldDialog
                                listing={listing}
                                onSold={reloadListing}
                                trigger={
                                    <Button
                                        data-testid="detail-mark-sold-btn"
                                        disabled={Math.max(0, (listing.quantity || 1) - (listing.sold_quantity || 0)) <= 0}
                                        className="w-full h-11 rounded-full aduku-yellow-btn hover:bg-aduku-yellow-600 disabled:opacity-50 font-semibold"
                                    >
                                        <CheckCircle2 className="w-4 h-4 mr-1.5" />
                                        {Math.max(0, (listing.quantity || 1) - (listing.sold_quantity || 0)) <= 0 ? "Sold out" : "Mark as Sold"}
                                    </Button>
                                }
                            />
                            <AlertDialog>
                                <AlertDialogTrigger asChild>
                                    <Button
                                        data-testid="detail-delete-btn"
                                        variant="outline"
                                        className="w-full h-11 rounded-full border-red-500/50 text-red-600 hover:bg-red-500 hover:text-white hover:border-red-500 font-semibold"
                                    >
                                        <Trash2 className="w-4 h-4 mr-1.5" /> Delete listing
                                    </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                    <AlertDialogHeader>
                                        <AlertDialogTitle>Delete this listing?</AlertDialogTitle>
                                        <AlertDialogDescription>
                                            &ldquo;{listing.title}&rdquo; will be permanently removed from Adukoo. Chat threads remain, but the ad will no longer be visible to buyers. This action can&apos;t be undone.
                                        </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                                        <AlertDialogAction data-testid="detail-delete-confirm" onClick={handleDelete} className="bg-red-500 hover:bg-red-600">Delete</AlertDialogAction>
                                    </AlertDialogFooter>
                                </AlertDialogContent>
                            </AlertDialog>
                        </div>
                    )}

                    {isOwner && (
                        <div className="bg-aduku-navy text-white rounded-xl p-5">
                            <div className="flex items-center gap-2 text-aduku-yellow font-display font-bold text-sm">
                                <Rocket className="w-4 h-4" /> Ad Boost
                            </div>
                            {boostDaysLeft(listing.boosted_until) > 0 ? (
                                <>
                                    <p className="text-xs text-white/75 mt-1.5">
                                        Your ad is pinned to the top for the next{" "}
                                        <b className="text-aduku-yellow">{boostDaysLeft(listing.boosted_until)} day{boostDaysLeft(listing.boosted_until) === 1 ? "" : "s"}</b>. Extend by another window below.
                                    </p>
                                </>
                            ) : (
                                <p className="text-xs text-white/75 mt-1.5">
                                    Pin this ad to the top of search results. Pick how long below.
                                </p>
                            )}
                            <div className="grid grid-cols-3 gap-2 mt-3">
                                {[7, 15, 30].map((d) => (
                                    <button
                                        key={d}
                                        type="button"
                                        data-testid={`detail-boost-duration-${d}`}
                                        onClick={() => setBoostChoice(d)}
                                        className={`h-14 rounded-xl border text-sm font-bold transition-colors ${
                                            boostChoice === d
                                                ? "border-aduku-yellow bg-aduku-yellow text-aduku-navy"
                                                : "border-white/20 bg-white/5 text-white hover:bg-white/10"
                                        }`}
                                    >
                                        <div className="font-display text-base font-black leading-none">{d}</div>
                                        <div className="text-[10px] uppercase tracking-wider opacity-80 mt-0.5">days</div>
                                    </button>
                                ))}
                            </div>
                            <Button
                                data-testid={boostDaysLeft(listing.boosted_until) > 0 ? "detail-extend-boost" : "detail-boost-btn"}
                                disabled={boostBusy}
                                onClick={() => triggerBoost(boostChoice)}
                                className="mt-3 w-full aduku-yellow-btn hover:bg-aduku-yellow-600 text-aduku-navy rounded-full"
                            >
                                <Rocket className="w-4 h-4 mr-1.5" />
                                {boostBusy
                                    ? "Boosting..."
                                    : boostDaysLeft(listing.boosted_until) > 0
                                        ? `Extend ${boostChoice} more days`
                                        : `Boost for ${boostChoice} days`}
                            </Button>
                        </div>
                    )}

                    {isAdmin && (
                        <div className="bg-red-50 border border-red-200 rounded-xl p-5">
                            <div className="flex items-center gap-2 text-red-700 font-display font-bold text-sm">
                                <Shield className="w-4 h-4" /> Admin moderation
                            </div>
                            <p className="text-xs text-red-700/80 mt-1">Remove this listing if it violates Adukoo policies.</p>
                            <AlertDialog>
                                <AlertDialogTrigger asChild>
                                    <Button data-testid="admin-remove-from-detail" className="mt-3 w-full bg-red-500 hover:bg-red-600 text-white rounded-full">
                                        <Trash2 className="w-4 h-4 mr-1.5" /> Remove inappropriate listing
                                    </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                    <AlertDialogHeader>
                                        <AlertDialogTitle>Remove this listing?</AlertDialogTitle>
                                        <AlertDialogDescription>This action cannot be undone. Related chats will also be deleted.</AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                                        <AlertDialogAction onClick={adminRemove} className="bg-red-500 hover:bg-red-600">Confirm remove</AlertDialogAction>
                                    </AlertDialogFooter>
                                </AlertDialogContent>
                            </AlertDialog>
                        </div>
                    )}

                    {/* v79.170 — In-house Adukoo Ads. Sidebar slot fills the
                        empty space below the price / contact CTAs on desktop
                        and slides in under the price card on mobile. */}
                    <AdukooAdSlot slot="listing_sidebar" />
                </aside>
            </div>

            {/* v79.208 — Bottom-of-listing revenue slot. Sits between the
                main product/aside grid and the reviews. Renders nothing
                when no ad targets this slot. */}
            <AdukooAdSlot slot="listing_bottom" className="mt-6" />

            {/* v79.94 — Ratings + threaded comments. Category-gated:
                jobs, job-seeker, and matrimony NEVER carry public
                ratings (dignity / privacy). Sponsored ads also opt out
                (no real seller relationship — the CTA sends the buyer
                to the partner site). Everything else — physical
                products, services, software, real estate — gets the
                review section for social proof. Digital stores use
                their own reviews component on the Store page. */}
            {listing && !listing.is_affiliate && canReview(listing.category) && (
                <ReviewSection listingId={listing.id} ownerId={listing.user_id} />
            )}

            {/* v79.193 — Admin edit audit log. Renders itself only when
                the viewer is the listing owner or an admin AND there's
                at least one recorded edit. Gives sellers full change
                history for trust + dispute resolution. */}
            {listing && (isOwner || isAdmin) && (
                <div className="max-w-6xl mx-auto px-4 sm:px-6">
                    <AdminAuditLog listingId={listing.id} canView={isOwner || isAdmin} />
                </div>
            )}

            <Disclaimer variant="ad" />

            {/* Image lightbox — click cover to open, pinch-to-zoom on mobile,
                +/- buttons on desktop, arrow keys / on-screen arrows for nav. */}
            {lightboxOpen && imgs[activeImg] && (
                <div
                    data-testid="listing-lightbox"
                    onClick={() => setLightboxOpen(false)}
                    className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4 overflow-auto touch-pan-y"
                >
                    <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setLightboxOpen(false); }}
                        data-testid="listing-lightbox-close"
                        className="absolute top-4 right-4 h-10 w-10 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center"
                        aria-label="Close zoom"
                    >
                        <X className="w-5 h-5" />
                    </button>
                    <div className="absolute top-4 left-4 flex gap-2">
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setZoom((z) => Math.max(0.5, +(z - 0.25).toFixed(2))); }}
                            data-testid="listing-lightbox-zoom-out"
                            className="h-10 px-3 rounded-full bg-white/15 hover:bg-white/25 text-white font-bold"
                        >−</button>
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setZoom(1); }}
                            data-testid="listing-lightbox-zoom-reset"
                            className="h-10 px-3 rounded-full bg-white/15 hover:bg-white/25 text-white text-xs font-semibold"
                        >Reset</button>
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setZoom((z) => Math.min(4, +(z + 0.25).toFixed(2))); }}
                            data-testid="listing-lightbox-zoom-in"
                            className="h-10 px-3 rounded-full bg-white/15 hover:bg-white/25 text-white font-bold"
                        >+</button>
                        <div className="h-10 px-3 rounded-full bg-white/10 text-white/80 text-xs font-semibold flex items-center">{Math.round(zoom * 100)}%</div>
                    </div>
                    {imgs.length > 1 && (
                        <>
                            <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); setActiveImg((i) => (i - 1 + imgs.length) % imgs.length); setZoom(1); }}
                                data-testid="listing-lightbox-prev"
                                className="absolute left-4 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center"
                                aria-label="Previous"
                            >
                                <ChevronLeft className="w-6 h-6" />
                            </button>
                            <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); setActiveImg((i) => (i + 1) % imgs.length); setZoom(1); }}
                                data-testid="listing-lightbox-next"
                                className="absolute right-4 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center"
                                aria-label="Next"
                            >
                                <ChevronRight className="w-6 h-6" />
                            </button>
                            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-white/15 text-white text-xs font-semibold">
                                {activeImg + 1} / {imgs.length}
                            </div>
                        </>
                    )}
                    <img
                        src={imgs[activeImg]}
                        alt={listing.title}
                        onClick={(e) => e.stopPropagation()}
                        onTouchStart={(e) => { e.stopPropagation(); onSwipeStart(e); }}
                        onTouchEnd={(e) => { e.stopPropagation(); onSwipeEnd(e); }}
                        onDoubleClick={(e) => { e.stopPropagation(); setZoom((z) => (z >= 2 ? 1 : 2)); }}
                        style={{ transform: `scale(${zoom})`, transition: "transform 0.15s" }}
                        className="max-w-[95vw] max-h-[85vh] object-contain cursor-zoom-in select-none touch-pan-y"
                    />
                </div>
            )}
        </div>
    );
}
